import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Arrow } from "konva/lib/shapes/Arrow";
import { Line } from "konva/lib/shapes/Line";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";

export class MeasurementLineKonva {
  static init(measurementLine: MeasurementLine): Group {
    const parentGroup = new Group();

    const lineGroup = new Group({
      x: measurementLine.x,
      y: measurementLine.y,
      rotation: measurementLine.rotation,
      height: measurementLine.height,
      width: measurementLine.width,
      text: measurementLine.referenceLength,
      draggable: false,
    });
    lineGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, measurementLine);
    lineGroup.setAttr("minWidth", 80);
    lineGroup.setAttr("minHeight", 30);
    const rect = new Rect({
      x: 0,
      width: measurementLine.width,
      height: measurementLine.height,
    });
    const arrow = new Arrow({
      points: [0, 0, measurementLine.width, 0],
      pointerLength: 10,
      pointerWidth: 10,
      fill: "black",
      stroke: "black",
      strokeWidth: 2,
      pointerAtBeginning: true,
    });
    const lineLeft = new Line({
      points: [-2, -measurementLine.height / 2, -2, measurementLine.height / 2],
      stroke: "black",
      strokeWidth: 2,
    });
    const lineRight = new Line({
      points: [measurementLine.width + 2, -measurementLine.height / 2, measurementLine.width + 2, measurementLine.height / 2],
      stroke: "black",
      strokeWidth: 2,
    });
    const measurementText = new Text({
      text: this.setMeasurementText(measurementLine),
      fontSize: 15,
    });
    const itemText = FloorplanItemTextKonva.init(measurementLine);
    const text = FloorplanItemTextKonva.getText(itemText);
    this.scaleText(text, lineGroup);
    FloorplanItemTextKonva.updateBackground(itemText);

    measurementText.x((measurementLine.width - measurementText.width()) / 2);

    arrow.y(rect.height() / 2);
    lineLeft.y(rect.height() / 2);
    lineRight.y(rect.height() / 2);

    lineGroup.add(measurementText);
    lineGroup.add(rect);
    lineGroup.add(arrow);
    lineGroup.add(lineLeft);

    lineGroup.add(lineRight);
    parentGroup.add(lineGroup, itemText);
    this.setTextPosition(itemText, lineGroup);

    lineGroup.on("transformstart", () => {
      itemText.hide();
    });

    lineGroup.on("transform", () => {
      lineGroup.width(lineGroup.width() * lineGroup.scaleX());
      lineGroup.height(lineGroup.height() * lineGroup.scaleY());

      rect.y(0);
      rect.height(lineGroup.height());
      rect.width(lineGroup.width());

      arrow.points([0, 0, lineGroup.width(), 0]);
      arrow.y(rect.height() / 2);

      lineLeft.points([-2, -lineGroup.height() / 2, -2, lineGroup.height() / 2]);
      lineLeft.y(rect.height() / 2);
      lineRight.points([lineGroup.width() + 2, -lineGroup.height() / 2, lineGroup.width() + 2, lineGroup.height() / 2]);
      lineRight.y(rect.height() / 2);

      measurementText.x((lineGroup.width() - measurementText.width()) / 2);
      this.scaleText(text, lineGroup);
      FloorplanItemTextKonva.updateBackground(itemText);
      lineGroup.scaleX(1);
      lineGroup.scaleY(1);
    });

    lineGroup.on("transformend", () => {
      measurementLine.updateForm(lineGroup.width(), lineGroup.height(), lineGroup.rotation(), lineGroup.x(), lineGroup.y());
      this.setTextPosition(itemText, lineGroup);
      measurementText.text(this.setMeasurementText(measurementLine));
      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    lineGroup.on("dragmove", () => {
      this.setTextPosition(itemText, lineGroup);
    });

    parentGroup.add(lineGroup, itemText);

    return parentGroup;
  }

  static refreshMeasurementText(measurementLine: MeasurementLine, group: Group) {
    const measurementLineGroup = group.getChildren()[0] as Konva.Group;
    const text = measurementLineGroup.getChildren()[0] as Text;
    text.text(this.setMeasurementText(measurementLine));
    text.x((measurementLine.width - text.width()) / 2);
  }

  static refreshItemText(measurementLine: MeasurementLine, group: Group) {
    const itemText = group.getChildren()[1] as Group;
    const lineGroup = group.getChildren()[0] as Konva.Group;
    FloorplanItemTextKonva.refreshText(measurementLine, itemText);
    this.setTextPosition(itemText, lineGroup);
  }

  static setMeasurementText(measurementLine: MeasurementLine) {
    return measurementLine.referenceLength ? measurementLine.referenceLength.toString() + " Meter" : measurementLine.text;
  }

  private static setTextPosition(itemText: Group, lineGroup: Group) {
    itemText.offsetY(0);
    itemText.offsetX(0);

    const item = lineGroup.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(itemText, lineGroup, item);
  }

  private static scaleText(itemText: Text, lineGroup: Group) {
    itemText.fontSize(lineGroup.height() / 3);
  }

  static refreshLength(measurementLine: MeasurementLine, group: Konva.Group) {
    const parentGroup = group.getChildren()[0] as Konva.Group;
    const rect = parentGroup.getChildren()[1] as Konva.Rect;
    const arrow = parentGroup.getChildren()[2] as Konva.Arrow;
    const lineRight = parentGroup.getChildren()[4] as Konva.Line;

    rect.width(measurementLine.width);
    arrow.points([0, 0, measurementLine.width, 0]);
    lineRight.points([rect.width() + 2, -rect.height() / 2, rect.width() + 2, rect.height() / 2]);
    this.refreshMeasurementText(measurementLine, group);
    this.refreshItemText(measurementLine, group);
  }
}
